import type { Client } from '@spree/storefront-api-v2-sdk'
import { IPaymentMethodsResult } from '@spree/storefront-api-v2-sdk/types/interfaces/PaymentMethod'
import { IToken } from '@spree/storefront-api-v2-sdk/types/interfaces/Token'
import { RAILS_API_URL } from 'config/apiUrl'
import { isNil } from 'lodash'
import { AcademyApplyCouponParams } from 'redux/slices/academy-onboarding-slice'
import { store } from 'redux/store'
import { PaymentSource } from 'redux/types'
import { normalizeJsonApi } from 'utils/normalizeJsonApi'

export interface StorefrontCommonAttributes {
  data: StorefrontBaseAttribute[] | StorefrontBaseAttribute | null
}

export interface StorefrontBaseAttribute {
  id: string
  type: string
}

export interface StorefrontCart {
  id: string
  type: string
  attributes: StorefrontCartAttributes
  relationships: StorefrontCartRelationships
}

export interface StorefrontCartAttributes {
  number: string
  itemTotal: number
  total: string
  shipTotal: string
  adjustmentTotal: string
  createdAt: string
  updatedAt: string
  completedAt: null | string
  includedTaxTotal: string
  additionalTaxTotal: string
  displayAdditionalTaxTotal: string
  displayIncludedTaxTotal: string
  taxTotal: string
  currency: string
  state: string
  token: string
  email: string
  displayItemTotal: string
  displayShipTotal: string
  displayAdjustmentTotal: string
  displayTaxTotal: string
  promoTotal: string
  displayPromoTotal: string
  itemCount: number
  specialInstructions: null
  displayTotal: string
  lineItemsNames: string
  gatewaySecureModeRedirectUrl: null
  pharmacistId: number | null
  orderType: string
  trackingLink: string
  itemTotalIfPackage: null
}

export interface StorefrontCartRelationships {
  lineItems: StorefrontRelationship
  variants: StorefrontRelationship
  promotions: StorefrontRelationship
  payments: StorefrontRelationship
  shipments: StorefrontRelationship
  user: StorefrontRelationship
  billingAddress: StorefrontRelationship
  shippingAddress: StorefrontRelationship
}

export interface StorefrontRelationship {
  data: StorefrontCommonAttributes
}

export interface StorefrontCheckoutParams {
  payment_source: Record<number, PaymentSource>
  order: {
    payments_attributes: {
      payment_method_id: number
      installments?: string
    }[]
  }
  iugu_card_id?: string
  orderToken: string
}

class StorefrontApiService {
  private spreeClient?: Promise<Client>

  /** The Spree SDK (~90 KB with its own axios) loads on first use, not in the bundle every page downloads. */
  client(): Promise<Client> {
    this.spreeClient ??= import('@spree/storefront-api-v2-sdk').then(({ makeClient }) =>
      makeClient({ host: RAILS_API_URL })
    )
    return this.spreeClient
  }

  getToken() {
    const state = store.getState()

    return state.auth.railsToken
  }

  async getPaymentMethods(orderToken?: string) {
    const bearerToken = this.getToken()

    if (!orderToken && !bearerToken) return null

    const token = (isNil(orderToken) ? { bearerToken } : { orderToken }) as IToken
    const paymentMethods: IPaymentMethodsResult = await (await this.client()).checkout.paymentMethods(token)

    return normalizeJsonApi(paymentMethods.success())
  }

  async updateCartVariant({ variantId, orderToken }: { variantId: number; orderToken: string }) {
    await (await this.client()).cart.emptyCart({ orderToken })
    const orderResult = await (
      await this.client()
    ).cart.addItem(
      { orderToken },
      {
        variant_id: variantId.toString(),
        quantity: 1,
        include: 'line_items'
      }
    )
    if (orderResult.isSuccess()) {
      const cart = await normalizeJsonApi(orderResult.success())
      return Promise.resolve(cart)
    } else {
      const failData = orderResult.fail()
      return Promise.reject(failData)
    }
  }

  async applyCoupon({ coupon, orderToken }: AcademyApplyCouponParams) {
    const orderResult = await (
      await this.client()
    ).cart.applyCouponCode(
      { orderToken },
      {
        coupon_code: coupon
      }
    )
    if (orderResult.isSuccess()) {
      return Promise.resolve(await normalizeJsonApi(orderResult.success()))
    } else {
      return Promise.reject(orderResult.fail())
    }
  }

  async checkout({ orderToken, ...params }: StorefrontCheckoutParams) {
    const bearerToken = this.getToken()

    if (!orderToken && !bearerToken) return null

    const token = (isNil(orderToken) ? { bearerToken } : { orderToken }) as IToken

    await (await this.client()).checkout.orderUpdate(token, params as any)
    const orderResult = await (await this.client()).checkout.complete(token)
    if (orderResult.isSuccess()) {
      return Promise.resolve(await normalizeJsonApi(orderResult.success()))
    } else {
      return Promise.reject(orderResult.fail())
    }
  }

  async getCompletedOrders() {
    const bearerToken = this.getToken()

    if (!bearerToken) return null

    const orders = await (
      await this.client()
    ).account.completedOrdersList(
      {
        bearerToken
      },
      {
        per_page: 1000
      }
    )

    return normalizeJsonApi(orders.success())
  }

  async getProducts() {
    const bearerToken = this.getToken()

    if (!bearerToken) return null

    const response = await (
      await this.client()
    ).products.list(
      {
        bearerToken
      },
      {
        include:
          'option_types.option_values,images,variants_including_master,variants_including_master.images,variants_including_master.option_values',
        per_page: 100
      }
    )

    return normalizeJsonApi(response.success())
  }

  async getTaxons() {
    const bearerToken = this.getToken()

    if (!bearerToken) return null

    const response = await (await this.client()).taxons.list()

    return normalizeJsonApi(response.success())
  }

  async phytotherapyCreateOrder(variant_id: string) {
    const bearerToken = this.getToken()

    if (!bearerToken) return null

    await (await this.client()).cart.create({ bearerToken })
    await (await this.client()).cart.emptyCart({ bearerToken })

    const response = await (
      await this.client()
    ).cart.addItem(
      { bearerToken },
      {
        variant_id,
        quantity: 1,
        include: 'line_items'
      }
    )

    return normalizeJsonApi(response.success())
  }
}

const storefrontApiService = new StorefrontApiService()

export default storefrontApiService
